import React from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';

/*
 * Inputs are 16px on phones so iOS does not zoom on focus, 13px from `sm` up.
 */
const CONTROL_BASE =
  'block rounded-xl border-0 bg-white text-[16px] text-ink ring-1 ring-inset ring-neutral-200 transition-shadow placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-600/30 disabled:cursor-not-allowed disabled:bg-neutral-50 disabled:text-neutral-400 aria-[invalid=true]:ring-primary-600/60 sm:text-[13px]';

/**
 * Controls fill their container unless the caller asks for a width. Tailwind
 * decides between two `w-` utilities by the order in its stylesheet, not by
 * the order in the class string, so `w-full` has to be left out rather than
 * merely preceded.
 */
const withWidth = (className: string) =>
  /(^|\s)(w-|min-w-|max-w-|flex-1|grow)/.test(className)
    ? CONTROL_BASE
    : `${CONTROL_BASE} w-full`;

const CONTROL = `${CONTROL_BASE} w-full`;

export const controlClass = CONTROL;

export const Field: React.FC<{
  label?: React.ReactNode;
  htmlFor?: string;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}> = ({ label, htmlFor, hint, error, required, className = '', children }) => (
  <div className={className}>
    {label && (
      <label htmlFor={htmlFor} className="mb-1.5 block text-[12px] text-neutral-500">
        {label}
        {required && <span className="ml-0.5 text-primary-600">*</span>}
      </label>
    )}
    {children}
    {error ? (
      <p className="mt-1.5 text-[12px] text-primary-700">{error}</p>
    ) : hint ? (
      <p className="mt-1.5 text-[12px] text-neutral-400">{hint}</p>
    ) : null}
  </div>
);

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className = '', ...props }, ref) => (
    <input ref={ref} className={`${withWidth(className)} h-10 px-3 sm:h-9 ${className}`} {...props} />
  ),
);
Input.displayName = 'Input';

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  ({ className = '', rows = 3, ...props }, ref) => (
    <textarea ref={ref} rows={rows} className={`${withWidth(className)} px-3 py-2 ${className}`} {...props} />
  ),
);
Textarea.displayName = 'Textarea';

export const Select = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(
  ({ className = '', children, ...props }, ref) => (
    <div className={`relative ${className}`}>
      <select ref={ref} className={`${CONTROL} h-10 appearance-none bg-none pl-3 pr-8 sm:h-9`} {...props}>
        {children}
      </select>
      <ChevronDown
        className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
        strokeWidth={1.75}
      />
    </div>
  ),
);
Select.displayName = 'Select';

/** Rounded grey search field */
export const SearchInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className = '', ...props }, ref) => (
    <div className={`relative ${className}`}>
      <Search
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
        strokeWidth={1.75}
      />
      <input
        ref={ref}
        type="search"
        className="block h-10 w-full rounded-full border-0 bg-neutral-100 pl-9 pr-3 text-[16px] text-ink placeholder:text-neutral-400 transition-shadow focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-600/25 sm:h-9 sm:text-[13px]"
        {...props}
      />
    </div>
  ),
);
SearchInput.displayName = 'SearchInput';

export const Checkbox: React.FC<
  Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> & { label?: React.ReactNode }
> = ({ label, className = '', id, ...props }) => (
  <label htmlFor={id} className={`inline-flex cursor-pointer items-center gap-2 text-[13px] text-ink ${className}`}>
    <span className="relative inline-flex h-4 w-4 flex-shrink-0">
      <input
        id={id}
        type="checkbox"
        className="peer h-4 w-4 cursor-pointer appearance-none rounded-[5px] border-0 bg-white ring-1 ring-inset ring-neutral-300 checked:bg-primary-600 checked:ring-primary-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 disabled:cursor-not-allowed disabled:opacity-50"
        {...props}
      />
      <Check
        className="pointer-events-none absolute inset-0 m-auto h-3 w-3 text-white opacity-0 peer-checked:opacity-100"
        strokeWidth={3}
      />
    </span>
    {label}
  </label>
);

export const Switch: React.FC<{
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
  className?: string;
}> = ({ checked, onChange, label, disabled = false, className = '' }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-10 flex-shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 disabled:cursor-not-allowed disabled:opacity-40 ${
      checked ? 'bg-primary-600' : 'bg-neutral-200'
    } ${className}`}
  >
    <span
      className={`inline-block h-5 w-5 rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.2)] transition-transform ${
        checked ? 'translate-x-[18px]' : 'translate-x-0.5'
      }`}
    />
  </button>
);
